import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { mergeMap, catchError, tap, switchMap, map } from 'rxjs/operators';

import { IProviderPreferenceResponse } from '../models/state.model';
import { POLAuthService } from '../services/auth.service';
import { ProviderService } from '../services/provider.service';
import { StorageService } from '../services/storage.service';
import { UserService } from '../services/user.service';
import * as polCoreActions from './core.actions';

@Injectable()
export class POLCoreEffects {
  constructor(
    private actions$: Actions,
    private authService: POLAuthService,
    private userService: UserService,
    private storageService: StorageService,
    private providerService: ProviderService
  ) { }
  logSignIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.actionSignInUser),
      tap((action) => {
        this.storageService.setItem('user-profile', JSON.stringify(action?.user?.profile));
      }),
      mergeMap((action) =>
        this.authService.getPOLUserProfile().pipe(
          mergeMap((data) => [polCoreActions.actionGetPOLUserProfileSuccess({ profile: data })]),
          catchError((error) => of(polCoreActions.actionGetPOLUserProfileFailure({ error })))
        )
      )
    )
  );

  actionGetPOLUserProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(polCoreActions.actionGetPOLUserProfileSuccess),
        tap((action) => {
          const existingUserProfile = JSON.parse(this.storageService.getItem('user-profile')) || {};
          existingUserProfile.providerType = action?.profile?.userProfile?.providerType;
          existingUserProfile.useAgreement = action?.profile?.useAgreement;
          this.storageService.setItem('user-profile', JSON.stringify(existingUserProfile));
          this.authService.hasUserAgreementData$.next({ hasUserAgreementData: true, useAgreementVaule: action?.profile?.useAgreement?.signatureRequired });
        })
      ),
    { dispatch: false }
  );

  actionGetUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.actionGetUserPreferences),
      mergeMap((action) =>
        this.userService.getUserPreferences().pipe(
          mergeMap((data) => {
            const user = { ...data };
            const providerFavorites = data.providerFavorites || [];
            delete user.providerFavorites;
            return [
              polCoreActions.actionGetUserPreferencesSuccess({ user }),
            ];
          }),
          catchError((error) => of(polCoreActions.actionGetUserPreferencesFailure({ error })))
        )
      )
    )
  );

  actionSignOutUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(polCoreActions.actionSignOutUser),
        tap(() => {
          this.storageService.clear();
          this.authService.signOutRedirect({ signOut: true });
        })
      ),
    { dispatch: false }
  );

  actionUpdateUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.actionUpdateUserPreferences),
      mergeMap((action) =>
        this.userService.updateUserPreferences(action.user).pipe(
          mergeMap((data) => {
            const user = { ...data };
            return [polCoreActions.actionUpdateUserPreferencesSuccess({ user })];
          }),
          catchError((error) => of(polCoreActions.actionUpdateUserPreferencesFailure({ error })))
        )
      )
    )
  );

  addProviderFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.actionAddFavoriteProvider),
      mergeMap((action) =>
        this.providerService.addFavoriteProvider(action.favoriteProvider).pipe(
          mergeMap((response) => [
            polCoreActions.actionAddFavoriteProviderSuccess({ favoriteProvider: action.favoriteProvider })
          ]),
          catchError((error) =>
            of(polCoreActions.addFavoriteProviderError({ error }))
          )
        )
      )
    )
  );

  removeProviderFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.actionRemoveFavoriteProvider),
      mergeMap((action) =>
        this.providerService.removeFavoriteProvider(action.favoriteProvider).pipe(
          mergeMap((response) => [
            polCoreActions.actionRemoveFavoriteProviderSuccess({ favoriteProvider: action.favoriteProvider })
          ]),
          catchError((error) =>
            of(
              polCoreActions.removeFavoriteProviderError({ error })
            )
          )
        )
      )
    )
  );

  getMyOaas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.actionGetMyOaas),
      switchMap(action => this.userService.getMyOaas()),
      map((myOaas) => polCoreActions.actionGetMyOaasSuccess({ myOaas })),
      catchError(error => of(polCoreActions.actionGetMyOaasFailure({ error })))
    )
  );

  /* Profile Page - Bookmark Providers Effects - START */

  getProviderPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.getFavoriteProviders),
      mergeMap(() =>
        this.providerService.getFavoriteProviders().pipe(
          mergeMap((favoriteProviders) => [polCoreActions.getFavoriteProvidersSuccess({ favoriteProviders })]),
          catchError((error) => of(polCoreActions.getFavoriteProvidersFailure({ error })))
        )
      )
    )
  );

  addFavoriteProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.addFavoriteProvider),
      mergeMap((action) =>
        this.providerService.addFavoriteProvider(action.favoriteProvider).pipe(
          mergeMap((response: IProviderPreferenceResponse) => [
            polCoreActions.addFavoriteProviderSuccess({ favoriteProviders: response })
          ]),
          catchError((error) =>
            of(polCoreActions.addFavoriteProviderError({ error }))
          )
        )
      )
    )
  );

  removeFavoriteProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(polCoreActions.removeFavoriteProvider),
      mergeMap((action) =>
        this.providerService.removeFavoriteProvider(action.favoriteProvider).pipe(
          mergeMap((response: IProviderPreferenceResponse) => [
            polCoreActions.removeFavoriteProviderSuccess({ favoriteProviders: response })
          ]),
          catchError((error) =>
            of(
              polCoreActions.removeFavoriteProviderError({ error })
            )
          )
        )
      )
    )
  );
}


/* Profile Page - Bookmark Providers Effects - END */
